/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { FacNavbarService } from './navbar.service';
import { environment } from './../../../environments/environment';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  ElementRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  FacAuthService,
  FacDropdownComponent,
  FacDropDownSearchItem,
  FacDropDownSearchResponse,
  FacUserService,
  DrawerContentService,
  DrawerDockItem,
  DOCS_PATHS,
  DocsService,
  UserInfo,
  CloudUserRoles,
  OnpremUserRoles,
  PopupDialogService,
  DropDownItem
} from '@foglight/angular-common';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { debounceTime, tap, map, switchMap, filter, takeUntil, first } from 'rxjs/operators';
import { AboutDialogComponent } from './about-dialog/about-dialog.component';
import { Router, NavigationEnd } from '@angular/router';
import { getDomainIconName } from 'src/app/views/alarm-template-ngrx/_pipes/domain-mapping.pipe';
import { Store } from '@ngrx/store';
import {
  isCloudFlagSelector,
  serverInfoSelector,
} from 'src/app/store/app.selectors';
import { SidebarService } from './../sidebar/services/sidebar.service';
import { cloneDeep } from 'lodash-es';
import { LayoutService } from '@services/layout.service';
import { UtilService } from '@services/util.service';
import { AUIPagesNoTheme } from '../sidebar/models/nav-item';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import {
  AI_CHAT_BOT,
  AiAlarmsService,
  AiOptimizerService,
} from '@foglight/angular-ai-common';
import { NavbarMenuItem } from './models/navbar.model';
import { NavigationMenuItem } from '../sidebar/models/navigation-menu-item';

@Component({
  selector: 'fc-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent implements OnInit, OnDestroy {
  private previousTheme = '';
  private destroy$ = new Subject<void>();
  private readonly imagesPath = `${environment.angularBaseUrl}/assets/images`;

  @Output() mouseEnterEmitter = new EventEmitter<HTMLElement>();
  @Output() mouseLeaveEmitter = new EventEmitter<HTMLElement>();
  @Output() displayOptionChange = new EventEmitter<string>();

  @ViewChild('menu') userMenu!: FacDropdownComponent;
  @ViewChild('headerMenu') headerMenu!: FacDropdownComponent;

  userInfo$!: Observable<UserInfo>;
  isUserInfoOpen = false;
  isActionMenuOpen = false;
  hoveringOverMSI = false;
  MSIIconHOver = true;
  EditMSI = false;
  serverNameExists = false;
  enterKeyPressed = false;
  MgmtServerIdFormControl = new UntypedFormControl('');
  startEditValue = '';
  userIsAdmin = false;
  showRegularUserTooltip = false;
  visible = false;
  isCloudFlag = false;
  isSaasAccepted = false;
  userIsAdminOrCloudOwner = false;
  cloudAppStatus = 'none-status';
  cloudAppDescription = '';
  aiChatStateStoreKey = 'AI_CHAT_DOCKDRAWER_STATE';
  pathToLogo = '';

  // Global Search
  searchFormControl = new UntypedFormControl('');
  filteredResponse$!: Observable<FacDropDownSearchResponse>;
  loading$ = new Subject<boolean>();
  serverNameExists$ = new BehaviorSubject<boolean>(false);
  isUserAnAdmin$ = new BehaviorSubject<boolean>(false);

  //Dropdown items
  userInfoItems: Array<NavbarMenuItem> = [];
  menuItemList: Array<NavbarMenuItem> = [];
  selectedItem!: NavbarMenuItem;
  headerMenuIcon = true;
  userLogoIcon = true;
  defaultHover = false;
  isMatMenuOpen = false;
  enteredButton = false;
  prevButtonTrigger: any;
  statusTooltip = '';

  //Theme Variables
  lightTheme = 'light-theme';
  darkTheme = 'dark-theme';
  contentWidth = '';

  constructor(
    private elementRef: ElementRef,
    private authService: FacAuthService,
    private userService: FacUserService,
    private navbarService: FacNavbarService,
    private router: Router,
    private renderer: Renderer2,
    private store: Store,
    private sidebarService: SidebarService,
    private cdRef: ChangeDetectorRef,
    private layoutService: LayoutService,
    private utilService: UtilService,
    private titleService: Title,
    private aiAlarmsService: AiAlarmsService,
    private aiOptimizerService: AiOptimizerService,
    public drawerContentService: DrawerContentService,
    private popupDialogService: PopupDialogService,
    private docsService: DocsService
  ) {
    this.router.errorHandler = (error: Error) => {
      console.log('Error occur when navigate. ' + error.message);
    };
  }

  ngOnInit(): void {
    this.setupDrawerContentWidth();
    this.checkApplicationStatus();
    this.handleAlarmTemplateThemeAndMenu();

    window.addEventListener('status-notification-event', () => {
      this.checkApplicationStatus();
    });

    this.navbarService.userSelectedTheme$.pipe(takeUntil(this.destroy$)).subscribe((theme) => {
      if (theme === 'light-theme') {
        this.statusTooltip = 'light-status-tooltip';
      } else {
        this.statusTooltip = 'dark-status-tooltip';
      }
    });

    this.userInfoItems = [
      { label: 'User Preferences', class: 'user_preferences_qa' },
      {
        label: 'Theme Selector',
        class: 'theme_selector_qa',
        showSelected: false,
        items: [
          { label: 'Light Theme', class: 'light_theme_qa' },
          { label: 'Dark Theme', class: 'dark_theme_qa' },
        ],
      },
      { label: 'Sign Out', class: 'sign_out_qa', icon: 'fac-trash' },
    ];
    
    // Disable theme selector on alarm template page (initial load)
    if (AUIPagesNoTheme.some(page => window.location.href.includes(`aui/${page}`))) {
      const themeSelectorItem = this.userInfoItems.find(item => item.label === 'Theme Selector');
      if (themeSelectorItem) {
        themeSelectorItem.disabled = true;
        if (themeSelectorItem.items) {
          themeSelectorItem.items.forEach(item => item.disabled = true);
        }
      }
    }

    this.menuItemList = [
      { label: 'Documentation', showSelected: false },
      { label: 'Contact Support', showSelected: false },
      { label: 'Ask Foglight Community', showSelected: false },
      { label: 'Send Feedback', showSelected: false },
      { label: 'About', showSelected: false },
    ];

    this.MgmtServerIdFormControl.disable();

    // Get server info from the store to check for init server instance name
    this.store
      .select(serverInfoSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((serverInfo): any => {
        if (
          serverInfo?.serverInstance?.name &&
          serverInfo?.serverInstance?.name != ''
        ) {
          this.serverNameExists$.next(true);
          this.MgmtServerIdFormControl.setValue(serverInfo.serverInstance.name);
          this.cdRef.markForCheck();
        }
      });

    this.store
      .select(isCloudFlagSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isCloud) => {
        this.isCloudFlag = isCloud;
        let menuItemIndex = this.menuItemList.findIndex(
          (x) => x.label === 'Contact Support'
        );

        if (this.isCloudFlag) {
          let index = this.userInfoItems.findIndex(
            (x) => x.label === 'User Preferences'
          );
            this.userInfoItems.splice(index, 0, {
              label: 'Account Settings',
              class: '',
            });
          this.menuItemList.splice(menuItemIndex, 0, {
            label: "What's New",
            showSelected: false,
          });
        } else {
          this.menuItemList.splice(menuItemIndex, 0, {
            label: 'Release Notes',
            showSelected: false,
          });
        }

        this.pathToLogo = `${this.imagesPath}${isCloud ? '/foglight_logo_cloud.svg' : '/foglight_logo_onprem.svg'}`;

        this.cdRef.markForCheck();
      });

    this.layoutService.verifyCloudAndSaasAcceptance();
    this.layoutService.showSideBar
      .pipe(takeUntil(this.destroy$))
      .subscribe((showNavbar) => {
        this.isSaasAccepted = showNavbar;
        this.cdRef.detectChanges();
      });

    if (this.userService) {
      this.userInfo$ = this.userService.getUserInfo();
    }

    if (this.userInfo$) {
      this.userInfo$
        .pipe(takeUntil(this.destroy$))
        .subscribe((userInfo: UserInfo) => {
          if (this.isCloudFlag) {
            this.userIsAdminOrCloudOwner =
              (userInfo.roles?.includes(CloudUserRoles.FoglightCloudOwner) ?? false) ||
              (userInfo.roles?.includes(CloudUserRoles.FoglightCloudAdministrator) ?? false);
          } else {
            this.userIsAdminOrCloudOwner =
              userInfo.roles?.includes(OnpremUserRoles.Administrator) ?? false;
          }
          this.isUserAnAdmin$.next(this.userIsAdminOrCloudOwner);
          this.userIsAdmin = this.userIsAdminOrCloudOwner;
          let currentTheme;
          if (userInfo.userSettings?.themes?.main) {
            currentTheme =
              userInfo.userSettings.themes.main == 'application_default'
                ? this.lightTheme
                : this.darkTheme;
          } else {
            currentTheme = this.lightTheme;
            this.updateTheme(currentTheme);
          }
          
          // Force light theme on alarm template page
          if (window.location.href.includes('aui/alarm-template') && currentTheme === this.darkTheme) {
            currentTheme = this.lightTheme;
            this.updateTheme(currentTheme);
          }
          localStorage.setItem('theme', currentTheme);

          const event = new CustomEvent('themeChanged', {
            detail: currentTheme,
          });
          window.dispatchEvent(event);

          this.navbarService.userSelectedTheme$.next(currentTheme);
          this.setSelectedItem(currentTheme);
          this.userInfoItems.splice(0, 0, {
            label: 'Hi,' + ' ' + userInfo.name + ' ! ',
            class: 'hi_user_qa',
            separator: true,
          });
        });
    }

    this.filteredResponse$ = this.searchFormControl.valueChanges.pipe(
      debounceTime(300),
      tap(() => this.loading$.next(true)),
      switchMap((value) => {
        if (value?.length === 0) {
          this.loading$.next(false);
          return of(new FacDropDownSearchResponse());
        }
        return this._getGlobalSearchResult(value);
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  // Handle navigation to/from alarm template page
  private handleAlarmTemplateThemeAndMenu(){
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const themeSelectorItem = this.userInfoItems?.find(item => item.label === 'Theme Selector');
        const currentTheme = localStorage.getItem('theme');
        
        if (AUIPagesNoTheme.some(page => window.location.href.includes(`aui/${page}`))) {
          if (currentTheme === this.darkTheme) {
            this.previousTheme = this.darkTheme;
            this.updateTheme(this.lightTheme);
            this.setSelectedItem(this.lightTheme);
          }
          if (themeSelectorItem) {
            themeSelectorItem.disabled = true;
            if (themeSelectorItem.items) {
              themeSelectorItem.items.forEach(item => item.disabled = true);
            }
          }
        } else {
          if (this.previousTheme && currentTheme === this.lightTheme) {
            this.updateTheme(this.previousTheme);
            this.setSelectedItem(this.previousTheme);
            this.previousTheme = ''; 
          }
          if (themeSelectorItem) {
            themeSelectorItem.disabled = false;
            if (themeSelectorItem.items) {
              themeSelectorItem.items.forEach(item => item.disabled = false);
            }

            const theme = localStorage.getItem('theme') || '';
            this.setSelectedItem(theme);
          }
        }
        this.cdRef.markForCheck();
      });
  }

  private setupDrawerContentWidth() {
    this.drawerContentService
      .setupDrawerContentWidth()
      .pipe(takeUntil(this.destroy$))
      .subscribe((width) => {
        if (width && width !== '') {
          this.contentWidth = width;
        }
      });
  }

  checkApplicationStatus() {
    try {
      const status = sessionStorage.getItem('status');
      const description = sessionStorage.getItem('description');

      if (status && status !== 'none') {
        this.cloudAppStatus = `${status}-status`;
        this.cloudAppDescription = description || '';
      } else {
        this.cloudAppStatus = 'none-status';
        this.cloudAppDescription = 'All Systems Operational';
      }

      this.cdRef.detectChanges();
    } catch (e) {
      console.log(e);
    }
  }

  get element(): HTMLElement {
    return this.elementRef.nativeElement as HTMLElement;
  }

  buttonEnter(trigger: MatMenuTrigger) {
    this.defaultHover = true;
    this.mouseEnterEmitter.emit(this.element);
    setTimeout(() => {
      if (this.prevButtonTrigger && this.prevButtonTrigger !== trigger) {
        this.prevButtonTrigger.closeMenu();
        this.prevButtonTrigger = trigger;
        this.isMatMenuOpen = false;
        trigger.openMenu();
      } else if (!this.isMatMenuOpen) {
        this.enteredButton = true;
        this.prevButtonTrigger = trigger;
        trigger.openMenu();
      } else {
        this.enteredButton = true;
        this.prevButtonTrigger = trigger;
      }
    }, 200);
  }

  buttonLeave(trigger: MatMenuTrigger) {
    this.defaultHover = false;
    this.mouseLeaveEmitter.emit(this.element);
    setTimeout(() => {
      if (this.enteredButton && !this.isMatMenuOpen) {
        trigger.closeMenu();
      } else if (!this.isMatMenuOpen) {
        trigger.closeMenu();
      } else {
        this.enteredButton = false;
      }
    }, 500);
  }

  menuEnter(event: MouseEvent) {
    this.isMatMenuOpen = true;
    this.displayOptionChange.emit('');
  }

  menuLeave(trigger: MatMenuTrigger) {
    setTimeout(() => {
      if (!this.enteredButton) {
        this.isMatMenuOpen = false;
        trigger.closeMenu();
      } else {
        this.isMatMenuOpen = false;
      }
    }, 300);
  }

  blurEventMethod() {
    if (!this.enterKeyPressed) {
      this.EditMSI = false;
      this.serverNameExists$
        .pipe(takeUntil(this.destroy$))
        .subscribe((nameExists) => {
          !!nameExists
            ? this.MgmtServerIdFormControl.setValue(this.startEditValue)
            : this.MgmtServerIdFormControl.setValue('');

          const msiInput = document.getElementById('MSI-input');
          if (msiInput) {
            const ctx = this.renderer.createElement('canvas').getContext('2d');
            const { fontStyle, fontVariant, fontWeight, fontSize, fontFamily } =
              msiInput.style;
            ctx.font =
              fontStyle +
              ' ' +
              fontVariant +
              ' ' +
              fontWeight +
              ' ' +
              fontSize +
              ' ' +
              fontFamily;
            msiInput.style.width =
              ctx!.measureText(this.startEditValue).width + 50 + 'px';

            msiInput.style.maxWidth = '220px';
            msiInput.blur();
          }
        });
    } else {
      this.EditMSI = false;
      const msiInput = document.getElementById('MSI-input');
      if (msiInput) {
        msiInput.style.maxWidth = '220px';
      }
      this.serverNameExists$
        .pipe(takeUntil(this.destroy$))
        .subscribe((nameExists) => {
          !!nameExists
            ? this.MgmtServerIdFormControl.setValue(this.startEditValue)
            : this.MgmtServerIdFormControl.setValue('');
        });
    }
  }

  focusEventMethod() {
    this.enterKeyPressed = false;
    this.startEditValue = this.MgmtServerIdFormControl.value;
    const msiInput = document.getElementById('MSI-input');
    if (msiInput) {
      msiInput.style.maxWidth = 'none';
    }
  }

  onMSIInputClickEditButton(input: string) {
    this.navbarService
      .updateServerInstanceName(input)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {});
    if (input !== '') {
      this.serverNameExists$.next(true);
    }
    this.EditMSI = true;
  }

  onMSIInputClickEnter(input: string) {
    this.enterKeyPressed = true;
    this.startEditValue = input;
    this.navbarService
      .updateServerInstanceName(input)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {});
    if (input !== '') {
      this.serverNameExists$.next(true);
    }
    this.EditMSI = false;
    const msiInput = document.getElementById('MSI-input');
    if (msiInput) {
      msiInput.blur();
    }
  }

  onDeleteMSIInput() {
    this.MgmtServerIdFormControl.setValue('');
    this.navbarService
      .updateServerInstanceName('')
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.serverNameExists$.next(false);
      });

    this.EditMSI = false;

    const msiInput = document.getElementById('MSI-input');

    if (msiInput) {
      msiInput.blur();
    }
  }

  focusOnMSIINPUT(num: Number) {
    if (num === 0) {
      this.EditMSI = !this.EditMSI;
    }

    setTimeout(() => {
      this.MgmtServerIdFormControl.enable();
      if (num === 1) {
        this.onMSIInputClickEditButton(this.MgmtServerIdFormControl.value);
        this.EditMSI = !this.EditMSI;
      }

      const msiInput = document.getElementById('MSI-input');
      if (msiInput) {
        msiInput.focus();
        msiInput.click();
      }
    });
  }

  onLoginClick() {
    this.authService.login();
  }

  onLogoutClick() {
    this.layoutService.logOut();
  }

  documentationOnClick() {
    const docsUrl = this.docsService.getDocsUrl(DOCS_PATHS.BLANK);
    window.open(docsUrl, '_blank');
  }

  supportOnClick() {
    if (this.isCloudFlag) {
      window.open('https://support.quest.com/foglight-cloud');
    } else
      window.open(
        `${environment.foglightBaseUrl}/aui/wcf?name=general-view-aui-wrapper&viewId=system:administration_setupsupport_supportbundles.supportDashboard`
      );
  }

  releaseNotesOnClick() {
    const docsUrl = this.docsService.getDocsUrl(
      DOCS_PATHS.ONPREM_RELEASE_NOTES
    );
    window.open(docsUrl, '_blank');
  }

  whatsNewOnClick() {
    const docsUrl = this.docsService.getDocsUrl(DOCS_PATHS.CLOUD_WHATS_NEW);
    window.open(docsUrl, '_blank');
  }

  feedbackOnClick() {
    window.open('https://www.quest.com/company/contact-us.aspx');
  }

  aboutOnClick() {  
    this.popupDialogService.openPopupDialog({
      width: '797px',
      height: '526px',
      disableClose: false,
      dynamicComponents: of(AboutDialogComponent),
      class: 'about-dialog-custom-style',
    });
  }

  communityOnClick() {
    window.open('https://www.quest.com/community/foglight/');
  }

  userInfoOpen() {
    this.isUserInfoOpen = true;
  }

  userInfoClose() {
    this.isUserInfoOpen = false;
  }

  actionMenuOpen() {
    this.isActionMenuOpen = true;
  }

  actionMenuClose() {
    this.isActionMenuOpen = false;
  }

  autoCompleteDisplay(item: FacDropDownSearchItem): string {
    if (!item) {
      return '';
    }
    return item.name;
  }

  resultSelected(item: FacDropDownSearchItem) {
    this.utilService.clearBreadCrumbHistory();
    this.router.navigateByUrl(item.viewPath);
  }

  private _getGlobalSearchResult(
    searchKey: string
  ): Observable<FacDropDownSearchResponse> {
    return this.navbarService.getGlobalSearchResult(searchKey).pipe(
      map(res => {
        this.loading$.next(false);
        res.resultItems?.forEach((item) => {
          item.iconName = getDomainIconName(item.iconName);
        });
        return res;
      })
    );
  }

  private getUserPreferencesLinks(): Observable<NavigationMenuItem[]> {
    return this.sidebarService.configItems$
      .pipe(
        takeUntil(this.destroy$),
        map((items) => {
          let data = cloneDeep(items);
          return data.filter(
            (item) => item.displayName === 'User Preferences'
          );
        })
      );
  }

  onItemSelect(event: any) {
    switch (event.label) {
      case 'Sign Out':
        this.onLogoutClick();
        break;

      case 'User Preferences':
        this.setSelectedItem(localStorage.getItem('theme') || '');

        this.getUserPreferencesLinks()
          .pipe(first())
          .subscribe((userPreferencesData) => {
            if (userPreferencesData.length > 0 && userPreferencesData[0].link) {
              this.router.navigateByUrl(userPreferencesData[0].link);
            }
          });
        break;
      case 'Light Theme':
        this.updateTheme(this.lightTheme);
        break;
      case 'Dark Theme':
        this.updateTheme(this.darkTheme);
        break;
      case 'Account Settings':
        this.loadAccountSettings();
        break;
      default:
        break;
    }
  }

  updateTheme(theme: string): void {
    let fullPath: string = window.location.href;
    let selectedTheme =
      theme == this.lightTheme ? 'application_default' : 'monitoring_default';

    this.navbarService
      .setUserSelectedTheme(selectedTheme)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        localStorage.setItem('theme', theme);
        this.navbarService.userSelectedTheme$.next(theme);

        const event = new CustomEvent('themeChanged', { detail: theme });
        window.dispatchEvent(event);
        if (fullPath.includes('/wcf')) {
          const currentUrl = this.router.url;
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigateByUrl(currentUrl);
              this.cdRef.detectChanges();
            });
        }
      });
  }

  setSelectedItem(theme: string): void {
    if (theme == this.lightTheme) {
      this.selectedItem = { label: 'Light Theme' };
    } else if (theme == this.darkTheme) {
      this.selectedItem = { label: 'Dark Theme' };
    }
  }

  onMenuItemSelect(event: DropDownItem): void {
    switch (event.label) {
      case 'Documentation':
        this.documentationOnClick();
        break;
      case 'Release Notes':
        this.releaseNotesOnClick();
        break;
      case "What's New":
        this.whatsNewOnClick();
        break;
      case 'Contact Support':
        this.supportOnClick();
        break;
      case 'Send Feedback':
        this.feedbackOnClick();
        break;
      case 'About':
        this.aboutOnClick();
        break;
      case 'Ask Foglight Community':
        this.communityOnClick();
        break;
    }
  }

  private clearSelectedSidebarItem(): void {
    sessionStorage.setItem('selectedItem', '');
    this.sidebarService.changeSelected(''); // Clear the selected item in the sidebar
  }

  moveToHomePage(): void {
    let homeLink = 'home';
    this.titleService.setTitle(`Welcome - Foglight`);
    this.clearSelectedSidebarItem();
    this.router.navigateByUrl(homeLink);
  }

  onFocusOut(e: FocusEvent, menuName: string): void {
    if (e.relatedTarget == null) {
      let elementName =
        menuName == 'userMenu' ? this.userMenu : this.headerMenu;
      elementName.hide();
    }
    if (menuName == 'userMenu') {
      this.userLogoIcon = true;
    } else if (menuName == 'headerMenu') {
      this.headerMenuIcon = true;
    }
  }

  loadAccountSettings(): void {
    this.router.navigate(['/settings/account-settings']);
  }

  onDockItemClick(item: DrawerDockItem): void {
    if (item.dockItemType === AI_CHAT_BOT.OPTIMIZER_CHAT_BOT) {
      this.aiOptimizerService.restoreAiOptimizerDrawer(item, this.contentWidth);
    } else {
      this.aiAlarmsService.restoreAiAlarmsDrawer(item, this.contentWidth);
    }
  }
}
